import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getMainnetTokenEquivalent, getTokenKey, type Token } from '@/shared/assets/tokens';
import useInterval from '@/shared/hooks/useInterval';
import { fetchTokenPrices } from '@/shared/utils/fetchTokenPrices';
import { noop } from '@/shared/utils/functions';

type Timeout = ReturnType<typeof setTimeout>;
type CoinGeckoPricesContextValue = {
  watchTokens: (tokens: Token[]) => () => void;
  getPrice: (token: Token) => number | undefined;
  isLoading: boolean;
};
const CoinGeckoPricesContext = createContext<CoinGeckoPricesContextValue | undefined>(undefined);

export const CoinGeckoPricesProvider = ({
  children,
  refreshInterval = 10_000,
}: {
  children: ReactNode;
  refreshInterval?: number;
}) => {
  const { current: watchedTokens } = useRef<Record<string, { count: number; token: Token }>>({});
  const [loadedPrices, setLoadedPrices] = useState<Record<string, number>>();

  const refreshPrices = useCallback(() => {
    const tokens = Object.values(watchedTokens)
      .filter((entry) => entry.count > 0)
      .map((entry) => entry.token);

    fetchTokenPrices(tokens.map(getMainnetTokenEquivalent))
      .then((prices) => {
        const newPrices = prices.reduce(
          (prev, cur) => ({ ...prev, [getTokenKey(cur)]: cur.usdPrice }),
          {} as Record<string, number>,
        );
        setLoadedPrices((oldPrices) => ({ ...oldPrices, ...newPrices }));
      })
      .catch(noop);
  }, []);

  // debounce refreshPrices call to prevent multiple calls during a single render
  const refreshPricesTimeout = useRef<Timeout | undefined>(undefined);
  const debouncedRefreshPrices = useCallback(() => {
    if (refreshPricesTimeout.current) clearTimeout(refreshPricesTimeout.current);
    refreshPricesTimeout.current = setTimeout(refreshPrices, 100);
  }, []);

  const watchTokens = useCallback((tokens: Token[]) => {
    const tokenKeys = tokens.map(getTokenKey);

    for (const [index, tokenKey] of tokenKeys.entries()) {
      if (!watchedTokens[tokenKey] || watchedTokens[tokenKey].count <= 0) {
        watchedTokens[tokenKey] = { token: tokens[index], count: 0 };
        debouncedRefreshPrices();
      }
      watchedTokens[tokenKey].count += 1;
    }

    return () => {
      for (const tokenKey of tokenKeys) {
        if (watchedTokens[tokenKey]) watchedTokens[tokenKey].count -= 1;
      }
    };
  }, []);

  const getPrice = useCallback(
    (token: Token) => loadedPrices?.[getTokenKey(getMainnetTokenEquivalent(token))],
    [loadedPrices],
  );

  useInterval(debouncedRefreshPrices, refreshInterval);

  const context = useMemo(
    () => ({
      watchTokens,
      getPrice,
      isLoading: loadedPrices === undefined,
    }),
    [watchTokens, getPrice, loadedPrices],
  );

  return (
    <CoinGeckoPricesContext.Provider value={context}>{children}</CoinGeckoPricesContext.Provider>
  );
};

export const useCoinGeckoPrices = () => {
  const context = useContext(CoinGeckoPricesContext);
  if (context === undefined) {
    throw new Error('useCoinGeckoPrices must be used within a CoinGeckoPricesContext');
  }

  return context;
};
